import React, { useEffect, useMemo, useRef, useState } from 'react';

// Simple, flicker-free SVG gauge with a debounced, eased needle animation.
// Props:
// - id: string (used to namespace gradient IDs)
// - value: number (0..100)
// - width, height: numbers (defaults 200x120)
// - colors: [startHex, midHex, endHex]
// - arcWidth: stroke width ratio (0..1 of radius), default 0.18
// - minAngle, maxAngle: degrees, default -110..110

function polarToCartesian(cx, cy, r, angleDeg) {
  const angle = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

function mix(a, b, t) { return Math.round(a + (b - a) * t); }
function hex(c) { return c.toString(16).padStart(2, '0'); }
function interpolateThree(colorA, colorB, colorC, t) {
  // t in [0,1]; first half A->B, second half B->C
  const parse = (h) => h.replace('#','').match(/.{2}/g).map(x => parseInt(x,16));
  const A = parse(colorA), B = parse(colorB), C = parse(colorC);
  if (t <= 0.5) {
    const k = t/0.5; return `#${hex(mix(A[0],B[0],k))}${hex(mix(A[1],B[1],k))}${hex(mix(A[2],B[2],k))}`;
  } else {
    const k = (t-0.5)/0.5; return `#${hex(mix(B[0],C[0],k))}${hex(mix(B[1],C[1],k))}${hex(mix(B[2],C[2],k))}`;
  }
}

export default function SvgGauge({
  id,
  value = 0,
  width = 200,
  height = 120,
  colors = ['#DC3545', '#FFA500', '#008000'],
  arcWidth = 0.16,
  minAngle = -110,
  maxAngle = 110,
  levels = 16,
}) {
  const cx = width / 2;
  const pad = Math.max(6, Math.min(16, Math.min(width, height) * 0.08));
  const r0 = Math.max(1, Math.min((width / 2) - pad, height - pad * 2));
  const safety = 8;
  const r = Math.max(1, (r0 - safety) / (1 + arcWidth / 2));
  const strokeW = Math.max(2, r * arcWidth);
  const cy = height - pad - (strokeW / 2);

  // For legacy look we render discrete filled ring segments (no stroke) to ensure crisp gaps

  // Smooth value with debounce + easing to prevent pulsing on frequent updates
  const [display, setDisplay] = useState(() => (typeof value === 'number' ? value : 0));
  const animRef = useRef(null);
  const debounceRef = useRef(null);
  const lastRef = useRef(display);
  const targetRef = useRef(display);

  useEffect(() => {
    const incoming = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : 0;
    const epsilon = 0.5; // ignore micro-changes
    const delta = Math.abs(incoming - lastRef.current);
    targetRef.current = incoming;

    if (delta < epsilon) {
      setDisplay(incoming);
      lastRef.current = incoming;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const from = lastRef.current;
      const to = targetRef.current;
      const start = performance.now();
      const duration = 700;
      const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
      const step = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = easeInOut(p);
        const next = from + (to - from) * eased;
        setDisplay(next);
        if (p < 1) animRef.current = requestAnimationFrame(step);
        else lastRef.current = to;
      };
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = requestAnimationFrame(step);
    }, 250);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const pct = Math.max(0, Math.min(100, display));
  const angle = minAngle + (maxAngle - minAngle) * (pct / 100);

  // Trim a tiny angle so rounded caps are visible even with tight layout
  const capDeg = Math.min(10, (strokeW / Math.max(1, r)) * 57.2958); // rad->deg
  const startA = minAngle + capDeg;
  const endA = maxAngle - capDeg;
  const arcPath = useMemo(() => describeArc(cx, cy, r, startA, endA), [cx, cy, r, startA, endA]);
  const safeBaseId = useMemo(() => {
    const raw = String(id || 'g');
    return raw.replace(/[^a-zA-Z0-9_-]/g, '_');
  }, [id]);
  const gradId = useMemo(() => `g-${safeBaseId}-linear`, [safeBaseId]);

  // Needle geometry
  const needleLen = r - strokeW * 2.0; // make needle shorter relative to thinner arc
  const baseR = strokeW * 0.45; // smaller hub
  const rot = (angle - 90) * (Math.PI / 180);
  const nx = cx + needleLen * Math.cos(rot);
  const ny = cy + needleLen * Math.sin(rot);
  // Triangular needle
  const side = baseR * 1;
  const left = {
    x: cx + side * Math.cos(rot + Math.PI/2),
    y: cy + side * Math.sin(rot + Math.PI/2)
  };
  const right = {
    x: cx + side * Math.cos(rot - Math.PI/2),
    y: cy + side * Math.sin(rot - Math.PI/2)
  };
  const needlePath = `M ${left.x} ${left.y} L ${nx} ${ny} L ${right.x} ${right.y} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2={width} y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colors[0]} />
          <stop offset="50%" stopColor={colors[1]} />
          <stop offset="100%" stopColor={colors[2]} />
        </linearGradient>
      </defs>

      {/* Continuous thin arc with rounded caps */}
      <path d={arcPath} stroke={`url(#${gradId})`} strokeWidth={strokeW} fill="none" strokeLinecap="round" strokeLinejoin="round" />

      {/* Needle (triangle) */}
      <path d={needlePath} fill={colors[1]} opacity={0.95} />
      {/* Hub */}
      <circle cx={cx} cy={cy} r={baseR} fill={colors[1]} />
    </svg>
  );
}


